import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@mui/material";
import {
  CommunicationPreferenceFormType,
  FormType,
  getField,
  getTicketRates,
  isAddressMandatory,
  Locales,
  NOTIFICATION_PHONE_NUMBER_BODY,
  QuestionType,
  TicketingFormCategory,
} from "@simplyk/common";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";

import { AddQuestionModal, DraftQuestionOption } from "./AddQuestionModal/AddQuestionModal";
import { CreateQuestionModal } from "./AddQuestionModal/CreateQuestionModal";
import { CommunicationPreferencesCard } from "./CommunicationPreferences/CommunicationPreferencesCard";
import { EditCommunicationPreferencesModal } from "./CommunicationPreferences/EditCommunicationPreferencesModal";
import { useCommunicationPreferencesPreset } from "./CommunicationPreferences/helpers/useCommunicationPreferencesPreset";
import { useCommunicationPreferencesFlag } from "./hooks/useCommunicationPreferencesFlag";
import { AttendeeSuggestionRow } from "./AttendeeSuggestionRow/AttendeeSuggestionRow";
import {
  buildPlaceholderQuestions,
  mapApiDataToRows,
  mapChoicesFromFormData,
  mapV3QuestionsToPreviewFormat,
  toV3Input,
  withDefaultMemberQuestions,
} from "./helpers/ticketingStepQuestionsV4Helpers";
import { buildExistingQuestionsAddedEvent } from "./helpers/buildExistingQuestionsAddedEvent";
import { getStepQuestionsV4Wording } from "./helpers/getStepQuestionsV4Wording";
import { getQuestionTypeSubtitle } from "./helpers/questionTypeIcons";
import { shouldSuggestGuestQuestions } from "./helpers/shouldSuggestGuestQuestions";
import { useCustomQuestionsTracking } from "./helpers/useCustomQuestionsTracking";
import { createScopedQuestionDragHandler } from "./helpers/useScopedQuestionDragReorder";
import { MembershipLinkedQuestionsListV4 } from "./MembershipLinkedQuestionsListV4";
import { TicketingLinkedQuestionsListV4 } from "./TicketingLinkedQuestionsListV4";
import {
  AddQuestionSelection,
  CreateQuestionEntryPoint,
  CreateQuestionFormData,
  CustomPropertyOption,
  DraftQuestion,
  QuestionScope,
  RateOption,
  TicketingFormQuestionRowV3,
  TicketingModalState,
  TicketingStepQuestionsV4FormData,
  GuestPresetOption,
  GUEST_EMAIL_PRESET_ID,
  GUEST_NAME_PRESET_ID,
} from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import {
  getFromLocalStorage,
  getGuestQuestionSuggestionDismissedKey,
  setInLocalStorage,
} from "@/common/helpers/localStorage";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useAnimatePreviewOnCommunicationPreferences } from "@/features/LiveFormEditor/AnimatePreview/useAnimatePreviewOnCommunicationPreferences";
import { useAnimatePreviewOnQuestionsEventsV3 } from "@/features/LiveFormEditor/AnimatePreview/useAnimatePreviewOnQuestionsEventsV3";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { isTicketingGenerateReceipt } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/helpers/isTicketingGenerateReceipt";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";

const EMPTY_SELECTED_IDS: string[] = [];
const EMPTY_DRAFTS: DraftQuestion[] = [];
const DRAFT_ID_PREFIX = "draft:";

export const TicketingLiveEditorStepQuestionsV4: FC = () => {
  const { apiFormObject: apiTicketing, setPreviewStepKey } = useLiveFormEditorContext<"ticketing">();
  const { organization } = useCurrentUserContext();
  const { t } = useTranslate();
  const logCustomQuestionsEvent = useCustomQuestionsTracking();
  const formContainerRef = useRef<HTMLDivElement>(null);

  const isMemberInfoMode = apiTicketing.formCategory === TicketingFormCategory.MembershipV2;

  const [modalState, setModalState] = useState<TicketingModalState>({ type: "closed" });
  const [isSaving, setIsSaving] = useState(false);

  const addModalScope = modalState.type === "add-question" ? modalState.scope : "buyer";
  const addModalSelectedIds = modalState.type === "add-question" ? modalState.selectedIds : EMPTY_SELECTED_IDS;
  const draftQuestions = modalState.type === "add-question" ? modalState.drafts : EMPTY_DRAFTS;

  const [isCommunicationPreferencesModalOpen, setIsCommunicationPreferencesModalOpen] = useState(false);

  const { isActive: isCommunicationPreferencesEnabled } = useCommunicationPreferencesFlag();

  const {
    focusCommunicationPreferences,
    focusAddedCommunicationPreferences,
    handleQuestionsFocus: handleCommunicationPreferencesFocus,
    hideArrow: hideCommunicationPreferencesArrow,
  } = useAnimatePreviewOnCommunicationPreferences();

  // The question is unique per form: once it is offered, the card is the only way to edit it.
  const {
    presets: communicationPreferencesPresets,
    addFromSelection: addCommunicationPreferences,
    removeFromForm: removeCommunicationPreferences,
    isOfferedOnForm: formOffersCommunicationPreferences,
  } = useCommunicationPreferencesPreset({
    formId: apiTicketing.id,
    formType: CommunicationPreferenceFormType.Ticketing,
    isEnabled: isCommunicationPreferencesEnabled,
    onAdded: focusAddedCommunicationPreferences,
    onRemoved: hideCommunicationPreferencesArrow,
  });

  const { data: questionsData, refetch: refetchQuestionsData } = trpc.getTicketingQuestionsStepData.useQuery({
    ticketingId: apiTicketing.id,
  });

  const { data: customPropertiesData, isLoading: isLoadingProperties } = trpc.getOrganizationCustomProperties.useQuery({
    locale: apiTicketing.locale,
  });

  const updateTicketingQuestionsV3 = trpc.updateTicketingQuestionsV3.useMutation();

  const rateOptions: RateOption[] = useMemo(
    () =>
      getTicketRates(apiTicketing.rates).map((rate) => ({
        value: rate.id,
        label: getField(rate.rateFields, apiTicketing.locale)?.title || "",
      })),
    [apiTicketing.rates, apiTicketing.locale]
  );

  const allRateIds = useMemo(() => rateOptions.map((r) => r.value), [rateOptions]);

  const ticketingGenerateReceipt = isTicketingGenerateReceipt(
    apiTicketing.rates?.map((rate) => ({ ...rate, isGeneratingReceipt: Boolean(rate.eligibleAmount) })) || [],
    false,
    apiTicketing.generateExtraDonationReceipt,
    Boolean(organization?.canGenerateReceipt)
  );
  const isAddressMandatoryValue = isAddressMandatory(organization, ticketingGenerateReceipt);

  const defaultValues: TicketingStepQuestionsV4FormData = useMemo(
    () => ({
      questions: questionsData?.questions ? mapApiDataToRows(questionsData.questions, rateOptions) : [],
      askForAddress: isAddressMandatoryValue
        ? true
        : Boolean(questionsData?.askForAddress ?? apiTicketing.askForAddress),
    }),
    [questionsData, isAddressMandatoryValue, apiTicketing.askForAddress, rateOptions]
  );

  const form = useForm<TicketingStepQuestionsV4FormData>({ defaultValues, values: defaultValues });

  useEffect(() => {
    if (!isMemberInfoMode || !questionsData?.questions) {
      return;
    }
    const currentRows = form.getValues("questions");
    const withDefaults = withDefaultMemberQuestions(currentRows, {
      name: t("dashboard", "donationForm.questionBodyDefaultValue.FullName"),
      email: t("dashboard", "email"),
    });
    if (withDefaults.length !== currentRows.length) {
      form.setValue("questions", withDefaults, { shouldDirty: true });
    }
  }, [isMemberInfoMode, questionsData, form, t]);

  const watchedQuestions = form.watch("questions");
  const localQuestions = useMemo(() => watchedQuestions ?? [], [watchedQuestions]);
  const askForAddress = form.watch("askForAddress");
  const activeQuestions = localQuestions.filter((q) => q.isActiveOnForm);

  useAnimatePreviewOnQuestionsEventsV3(formContainerRef, activeQuestions, allRateIds);

  const questionNamesOnForm = useMemo(() => localQuestions.map((q) => q.body), [localQuestions]);

  const anySmsChannelOn = Boolean(
    apiTicketing.outbidSmsEnabled || apiTicketing.reminder1SmsEnabled || apiTicketing.reminder2SmsEnabled
  );

  const existingCustomProperties: CustomPropertyOption[] = useMemo(() => {
    const apiProperties = customPropertiesData?.customProperties ?? [];
    const scopesByPropertyId = new Map<string, QuestionScope[]>();
    for (const question of activeQuestions) {
      if (question.crmCustomPropertyId) {
        const scopes = scopesByPropertyId.get(question.crmCustomPropertyId) ?? [];
        scopesByPropertyId.set(question.crmCustomPropertyId, [...scopes, question.scope]);
      }
    }

    return apiProperties.map((p) => ({
      ...p,
      scopesOnForm: scopesByPropertyId.get(p.id) ?? [],
    }));
  }, [customPropertiesData, activeQuestions]);

  const notificationPhoneLabel = NOTIFICATION_PHONE_NUMBER_BODY[(apiTicketing.locale as Locales) || Locales.EN];
  const hasActiveNotificationPhoneQuestion = activeQuestions.some(
    (q) => q.scope === "buyer" && q.type === QuestionType.NotificationPhoneNumber
  );
  const showNotificationPhoneOption = anySmsChannelOn && !hasActiveNotificationPhoneQuestion;
  const hasActiveGuestAddressQuestion = activeQuestions.some(
    (q) => q.scope === "participant" && q.type === QuestionType.Address
  );

  const guestPresets: GuestPresetOption[] = useMemo(() => {
    const isMembership = apiTicketing.formCategory === TicketingFormCategory.MembershipV2;
    const hasPersistedGuestQuestions = localQuestions.some((q) => q.scope === "participant" && q.questionId !== null);
    if (isMembership && !isMemberInfoMode && !hasPersistedGuestQuestions) {
      return [];
    }

    const presets: GuestPresetOption[] = [];

    const hasGuestName = activeQuestions.some((q) => q.scope === "participant" && q.type === QuestionType.Name);
    if (!hasGuestName) {
      presets.push({
        id: GUEST_NAME_PRESET_ID,
        label: t("dashboard", isMemberInfoMode ? "stepQuestionsV4.memberFullName" : "stepQuestionsV4.attendeeName"),
        questionType: QuestionType.Name,
      });
    }

    const hasGuestEmail = activeQuestions.some((q) => q.scope === "participant" && q.type === QuestionType.Email);
    if (!hasGuestEmail) {
      presets.push({
        id: GUEST_EMAIL_PRESET_ID,
        label: t("dashboard", isMemberInfoMode ? "stepQuestionsV4.memberEmail" : "stepQuestionsV4.attendeeEmail"),
        questionType: QuestionType.Email,
      });
    }

    return presets;
  }, [activeQuestions, localQuestions, apiTicketing.formCategory, isMemberInfoMode, t]);

  const rateQuestionsForPreview = useMemo(
    () => getTicketRates(apiTicketing.rates).map((rate) => ({ id: v4(), rateId: rate.id })),
    [apiTicketing.rates]
  );

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.askForAddress = formData.askForAddress ?? false;

      const participantQuestions = mapV3QuestionsToPreviewFormat(
        (formData.questions ?? []).filter((q) => q.scope === "participant"),
        apiTicketing.locale,
        rateQuestionsForPreview
      );

      const buyerQuestions = mapV3QuestionsToPreviewFormat(
        (formData.questions ?? []).filter((q) => q.scope === "buyer"),
        apiTicketing.locale,
        []
      );

      if (!participantQuestions.length && !buyerQuestions.length) {
        liveDataToMutate.ticketing.questions = buildPlaceholderQuestions(rateQuestionsForPreview);
        liveDataToMutate.placeholders.isGuestQuestions = true;
        liveDataToMutate.placeholders.isBuyerQuestions = true;
        return;
      }

      liveDataToMutate.ticketing.questions = [...participantQuestions, ...buyerQuestions];
      liveDataToMutate.placeholders.isGuestQuestions = participantQuestions.length === 0;
      liveDataToMutate.placeholders.isBuyerQuestions = buyerQuestions.length === 0;
    },
  });

  const saveQuestions = async (questions: TicketingFormQuestionRowV3[], askForAddressValue: boolean) => {
    setIsSaving(true);
    try {
      const result = await updateTicketingQuestionsV3.mutateAsync({
        ticketingId: apiTicketing.id,
        questions: toV3Input(questions),
        askForAddress: askForAddressValue,
      });
      await refetchQuestionsData();
      return result.data ?? null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteQuestion = (localId: string) => {
    const question = localQuestions.find((q) => q.localId === localId);
    if (!question) {
      return;
    }

    if (question.isNew) {
      const updatedQuestions = localQuestions.filter((q) => q.localId !== localId);
      form.setValue("questions", updatedQuestions, { shouldDirty: true });
      return;
    }

    const updatedQuestions = localQuestions.map((q) => (q.localId === localId ? { ...q, isActiveOnForm: false } : q));
    form.setValue("questions", updatedQuestions, { shouldDirty: true });
  };

  const openCreateModal = (
    scope: QuestionScope,
    selectedIds: string[],
    prefillBody?: string,
    entryPoint?: CreateQuestionEntryPoint
  ) => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionCreationStarted);
    setModalState({
      type: "create-new",
      scope,
      prefillBody,
      entryPoint,
      returnTo: { scope: addModalScope, selectedIds, drafts: draftQuestions },
    });
  };

  const handleOpenAddModal = (scope: QuestionScope) => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionsModalOpened);
    setModalState({ type: "add-question", scope, selectedIds: [], drafts: [] });
  };

  const handleQuestionSearch = useCallback(
    (audience: QuestionScope, hasResults: boolean) => {
      logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionSearched, {
        audience: audience === "buyer" ? "buyer" : "guest",
        has_results: hasResults,
      });
    },
    [logCustomQuestionsEvent]
  );

  const buildCreatedQuestion = (data: CreateQuestionFormData): TicketingFormQuestionRowV3 => ({
    localId: v4(),
    questionId: null,
    crmCustomPropertyId: data.matchedCustomProperty?.id ?? null,
    body: data.body,
    type: data.type,
    required: data.required,
    scope: data.scope ?? "buyer",
    choices: mapChoicesFromFormData(data.choicesWithMaximum ?? data.choices),
    sortIndex: 0,
    isActiveOnForm: true,
    isNew: true,
    isDirty: true,
    askForAllRates: data.askForAllRates ?? true,
    ratesAssigned: data.ratesAssigned ?? [],
    isMaximumActive: data.isMaximumActive ?? false,
    countAlreadyAnswered: 0,
  });

  const handleCreateQuestion = (data: CreateQuestionFormData) => {
    if (modalState.type !== "create-new") {
      return;
    }

    const scope = data.scope ?? "buyer";
    if (data.matchedCustomProperty) {
      logCustomQuestionsEvent(AmplitudeEvents.ExistingCustomQuestionAdded, {
        questions_added_count: 1,
        source: "creation-name-match",
      });
    } else {
      logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionCreated, {
        question_audience: scope,
        response_format: data.type,
        is_required: data.required,
        ...(modalState.entryPoint && { entry_point: modalState.entryPoint }),
      });
    }
    if (scope === "participant") {
      logCustomQuestionsEvent(AmplitudeEvents.GuestQuestionAdded, {
        questions_added_count: 1,
        source: data.matchedCustomProperty ? "creation-name-match" : "creation",
      });
    }

    const draft: DraftQuestion = { id: `${DRAFT_ID_PREFIX}${v4()}`, data: { ...data, scope } };
    const { returnTo } = modalState;
    setModalState({
      type: "add-question",
      scope: returnTo.scope,
      selectedIds: [...returnTo.selectedIds, draft.id],
      drafts: [...returnTo.drafts, draft],
    });
  };

  const buildGuestPresetQuestion = (preset: GuestPresetOption): TicketingFormQuestionRowV3 => ({
    localId: v4(),
    questionId: null,
    crmCustomPropertyId: null,
    body:
      preset.questionType === QuestionType.Name
        ? t("dashboard", "donationForm.questionBodyDefaultValue.FullName")
        : t("dashboard", "email"),
    type: preset.questionType,
    required: false,
    scope: "participant" as const,
    choices: [],
    sortIndex: 0,
    isActiveOnForm: true,
    isNew: true,
    isDirty: true,
    askForAllRates: true,
    ratesAssigned: [],
    isMaximumActive: false,
    countAlreadyAnswered: 0,
  });

  const handleAddSelected = (selection: AddQuestionSelection) => {
    if (selection.addFullAddress) {
      form.setValue("askForAddress", true, { shouldDirty: true });
      setPreviewStepKey("payment");
    }

    void addCommunicationPreferences(selection);

    const buildCrmQuestion = (property: CustomPropertyOption, scope: QuestionScope): TicketingFormQuestionRowV3 => ({
      localId: v4(),
      questionId: null,
      crmCustomPropertyId: property.id,
      body: property.body,
      type: property.type,
      required: false,
      scope,
      choices: property.choices.map((c, idx) => ({
        body: c.body,
        sortIndex: idx,
        maximum: null,
      })),
      sortIndex: 0,
      isActiveOnForm: true,
      isNew: true,
      isDirty: true,
      askForAllRates: true,
      ratesAssigned: [],
      isMaximumActive: false,
      countAlreadyAnswered: 0,
    });

    const findProperty = (id: string) => existingCustomProperties.find((p) => p.id === id);
    const crmQuestions: TicketingFormQuestionRowV3[] = [
      ...selection.buyerPropertyIds
        .map(findProperty)
        .filter((property): property is CustomPropertyOption => Boolean(property))
        .map((property) => buildCrmQuestion(property, "buyer")),
      ...selection.guestPropertyIds
        .map(findProperty)
        .filter((property): property is CustomPropertyOption => Boolean(property))
        .map((property) => buildCrmQuestion(property, "participant")),
    ];

    const phoneNumberQuestions: TicketingFormQuestionRowV3[] = [
      ...(selection.addPhoneNumber ? [{ scope: "buyer" as const }] : []),
      ...(selection.addGuestPhoneNumber ? [{ scope: "participant" as const }] : []),
    ].map(({ scope }) => ({
      localId: v4(),
      questionId: null,
      crmCustomPropertyId: null,
      body: t("dashboard", "donationForm.questionBodyDefaultValue.PhoneNumber"),
      type: QuestionType.PhoneNumber,
      required: false,
      scope,
      choices: [],
      sortIndex: 0,
      isActiveOnForm: true,
      isNew: true,
      isDirty: true,
      askForAllRates: true,
      ratesAssigned: [],
      isMaximumActive: false,
      countAlreadyAnswered: 0,
    }));

    const notificationPhoneQuestions: TicketingFormQuestionRowV3[] = selection.addNotificationPhoneNumber
      ? [
          {
            localId: v4(),
            questionId: null,
            crmCustomPropertyId: null,
            body: notificationPhoneLabel,
            type: QuestionType.NotificationPhoneNumber,
            required: false,
            scope: "buyer" as const,
            choices: [],
            sortIndex: 0,
            isActiveOnForm: true,
            isNew: true,
            isDirty: true,
            askForAllRates: true,
            ratesAssigned: [],
            isMaximumActive: false,
            countAlreadyAnswered: 0,
          },
        ]
      : [];

    const guestAddressQuestions: TicketingFormQuestionRowV3[] = selection.addGuestFullAddress
      ? [
          {
            localId: v4(),
            questionId: null,
            crmCustomPropertyId: null,
            body: t("dashboard", "stepQuestionsV4.dialog.fullAddress"),
            type: QuestionType.Address,
            required: true,
            scope: "participant",
            choices: [],
            sortIndex: 0,
            isActiveOnForm: true,
            isNew: true,
            isDirty: true,
            askForAllRates: true,
            ratesAssigned: [],
            isMaximumActive: false,
            countAlreadyAnswered: 0,
          },
        ]
      : [];

    const guestQuestions: TicketingFormQuestionRowV3[] = selection.guestPresetIds
      .map((presetId) => guestPresets.find((p) => p.id === presetId))
      .filter((preset): preset is GuestPresetOption => Boolean(preset))
      .map(buildGuestPresetQuestion);

    const reusedQuestions = [
      ...guestQuestions,
      ...phoneNumberQuestions,
      ...notificationPhoneQuestions,
      ...crmQuestions,
      ...guestAddressQuestions,
    ];
    const createdQuestions: TicketingFormQuestionRowV3[] = selection.draftIds
      .map((draftId) => draftQuestions.find((draft) => draft.id === draftId))
      .filter((draft): draft is DraftQuestion => Boolean(draft))
      .map((draft) => buildCreatedQuestion(draft.data));

    const addedQuestions = [...reusedQuestions, ...createdQuestions];
    if (addedQuestions.length > 0) {
      form.setValue("questions", [...localQuestions, ...addedQuestions], { shouldDirty: true });
    }
    const existingQuestionsAddedEvent = buildExistingQuestionsAddedEvent({
      reusedQuestionCount: reusedQuestions.length,
      addFullAddress: selection.addFullAddress,
    });
    if (existingQuestionsAddedEvent) {
      logCustomQuestionsEvent(AmplitudeEvents.ExistingCustomQuestionAdded, existingQuestionsAddedEvent);
    }
    const guestQuestionsAddedCount = reusedQuestions.filter((question) => question.scope === "participant").length;
    if (guestQuestionsAddedCount > 0) {
      logCustomQuestionsEvent(AmplitudeEvents.GuestQuestionAdded, {
        questions_added_count: guestQuestionsAddedCount,
        source: "modal",
      });
    }
    setModalState({ type: "closed" });
  };

  const handleEditQuestion = (question: TicketingFormQuestionRowV3) => {
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionEditionStarted, {
      question_audience: question.scope,
      response_format: question.type,
    });
    setModalState({ type: "edit", question });
  };

  const handleUpdateQuestion = (data: CreateQuestionFormData) => {
    if (modalState.type !== "edit") {
      return;
    }

    const choices = data.choicesWithMaximum ?? data.choices;
    const updatedScope = data.scope ?? "buyer";
    const isBuyerGuestChanged = modalState.question.scope !== updatedScope;

    const updatedQuestions = localQuestions.map((q) =>
      q.localId === modalState.question.localId
        ? {
            ...q,
            body: data.body,
            type: data.type,
            required: data.required,
            scope: updatedScope,
            askForAllRates: data.askForAllRates ?? true,
            ratesAssigned: data.ratesAssigned ?? [],
            isMaximumActive: data.isMaximumActive ?? false,
            choices: mapChoicesFromFormData(choices),
            isDirty: true,
          }
        : q
    );

    form.setValue("questions", updatedQuestions, { shouldDirty: true });
    logCustomQuestionsEvent(AmplitudeEvents.CustomQuestionEdited, {
      question_audience: updatedScope,
      question_is_buyer_guest_changed: isBuyerGuestChanged,
      response_format: data.type,
      is_required: data.required,
    });
    setModalState({ type: "closed" });
  };

  const handleCloseModal = () => {
    setModalState({ type: "closed" });
  };

  const buyerQuestions = useMemo(() => activeQuestions.filter((q) => q.scope === "buyer"), [activeQuestions]);
  const attendeeQuestions = useMemo(() => activeQuestions.filter((q) => q.scope === "participant"), [activeQuestions]);

  const suggestGuestQuestions = shouldSuggestGuestQuestions({
    isEventForm: apiTicketing.formCategory === TicketingFormCategory.Event,
    formTexts: [
      getField(apiTicketing.ticketingFields, apiTicketing.locale)?.title || "",
      ...(apiTicketing.rates ?? []).map((rate) => getField(rate.rateFields, apiTicketing.locale)?.title || ""),
    ],
    ticketTypesCount: apiTicketing.rates?.length ?? 0,
    hasGroupTickets: (apiTicketing.rates ?? []).some((rate) => (rate.attendeesByTicket ?? 0) > 1),
    hasSeats: apiTicketing.seats !== null,
    hasBuyerCustomQuestions: buyerQuestions.length > 0,
    hasGuestQuestions: attendeeQuestions.length > 0,
  });

  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<string[]>(() =>
    [GUEST_NAME_PRESET_ID, GUEST_EMAIL_PRESET_ID].filter((suggestionId) =>
      Boolean(getFromLocalStorage?.(getGuestQuestionSuggestionDismissedKey(apiTicketing.id, suggestionId), false, true))
    )
  );

  const visibleSuggestions = suggestGuestQuestions
    ? guestPresets.filter((preset) => !dismissedSuggestionIds.includes(preset.id))
    : [];

  const getSuggestionType = (presetId: string) => (presetId === GUEST_NAME_PRESET_ID ? "guest_name" : "guest_email");

  const hasLoggedSuggestionsRef = useRef(false);
  const visibleSuggestionTypes = visibleSuggestions.map((preset) => getSuggestionType(preset.id)).join(",");
  useEffect(() => {
    if (visibleSuggestionTypes && !hasLoggedSuggestionsRef.current) {
      hasLoggedSuggestionsRef.current = true;
      logCustomQuestionsEvent(AmplitudeEvents.GuestQuestionSuggested, {
        suggestion_types: visibleSuggestionTypes.split(","),
      });
    }
  }, [visibleSuggestionTypes, logCustomQuestionsEvent]);

  const handleDismissSuggestion = (preset: GuestPresetOption) => {
    setInLocalStorage?.(getGuestQuestionSuggestionDismissedKey(apiTicketing.id, preset.id), true);
    setDismissedSuggestionIds((previous) => [...previous, preset.id]);
    logCustomQuestionsEvent(AmplitudeEvents.GuestQuestionSuggestionDismissed, {
      suggestion_type: getSuggestionType(preset.id),
    });
  };

  const handleAddSuggestion = (preset: GuestPresetOption) => {
    form.setValue("questions", [...localQuestions, buildGuestPresetQuestion(preset)], { shouldDirty: true });
    logCustomQuestionsEvent(AmplitudeEvents.ExistingCustomQuestionAdded, {
      questions_added_count: 1,
      source: "suggestion",
      suggestion_type: getSuggestionType(preset.id),
    });
    logCustomQuestionsEvent(AmplitudeEvents.GuestQuestionAdded, {
      questions_added_count: 1,
      source: "suggestion",
      suggestion_type: getSuggestionType(preset.id),
    });
  };

  const handleBuyerDragEnd = createScopedQuestionDragHandler(localQuestions, "buyer", form.setValue);
  const handleGuestDragEnd = createScopedQuestionDragHandler(localQuestions, "participant", form.setValue);

  const hasActiveBuyerPhoneQuestion = buyerQuestions.some((q) => q.type === QuestionType.PhoneNumber);
  const hasActiveGuestPhoneQuestion = attendeeQuestions.some((q) => q.type === QuestionType.PhoneNumber);
  const hasSelectableBuyerPhoneProperty = existingCustomProperties.some(
    (property) =>
      property.type === QuestionType.PhoneNumber &&
      property.scope === "buyer" &&
      !property.scopesOnForm.includes("buyer")
  );
  const hasSelectableGuestPhoneProperty = existingCustomProperties.some(
    (property) =>
      property.type === QuestionType.PhoneNumber &&
      property.scope === "participant" &&
      !property.scopesOnForm.includes("participant")
  );

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<TicketingStepQuestionsV4FormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      try {
        const fresh = await saveQuestions(formData.questions, formData.askForAddress);
        return { data: fresh ?? apiTicketing, error: null };
      } catch {
        return { data: null, error: { code: "save_questions_failed", message: "Failed to save questions" } };
      }
    },
  });

  const editingQuestion = modalState.type === "edit" ? modalState.question : null;
  const createModalScope = modalState.type === "create-new" ? modalState.scope : null;
  const createModalPrefillBody = modalState.type === "create-new" ? modalState.prefillBody : undefined;

  const communicationPreferencesCard = isCommunicationPreferencesEnabled ? (
    <CommunicationPreferencesCard
      formId={apiTicketing.id}
      formType={CommunicationPreferenceFormType.Ticketing}
      onEdit={() => {
        setIsCommunicationPreferencesModalOpen(true);
        focusCommunicationPreferences();
      }}
      onDelete={removeCommunicationPreferences}
      // Always rendered last on the public form, so it lines up with the fixed buyer cards
      showDisabledReorderHandle={buyerQuestions.length > 1}
    />
  ) : undefined;

  const getSmsLabelTooltip = (question: TicketingFormQuestionRowV3) => {
    if (question.type !== QuestionType.NotificationPhoneNumber) {
      return undefined;
    }
    const audienceKey =
      apiTicketing.formCategory === TicketingFormCategory.Auction
        ? "ticketing.automatedCommunications.smsOptOut.audience.bidders"
        : "ticketing.automatedCommunications.smsOptOut.audience.buyers";
    return t("dashboard", "ticketing.automatedCommunications.smsOptOut.questionTooltip", {
      audience: t("dashboard", audienceKey),
    });
  };

  const getSmsDeleteDisabledTooltip = (question: TicketingFormQuestionRowV3) => {
    if (question.type !== QuestionType.NotificationPhoneNumber) {
      return undefined;
    }
    return anySmsChannelOn ? t("dashboard", "ticketing.automatedCommunications.smsOptOut.deleteBlocked") : undefined;
  };

  const isSwappingWithCreateModal =
    modalState.type === "create-new" || (modalState.type === "add-question" && modalState.drafts.length > 0);

  const stagedDrafts = modalState.type === "create-new" ? modalState.returnTo.drafts : draftQuestions;
  const isAttendeeNameStaged = stagedDrafts.some(
    (draft) => draft.data.scope === "participant" && draft.data.type === QuestionType.Name
  );
  const hasAttendeeNameOnForm = attendeeQuestions.some((question) => question.type === QuestionType.Name);
  const canAddAttendeeName = !isAttendeeNameStaged && !hasAttendeeNameOnForm;

  const modalGuestPresets = isAttendeeNameStaged
    ? guestPresets.filter((preset) => preset.id !== GUEST_NAME_PRESET_ID)
    : guestPresets;

  const draftQuestionOptions: DraftQuestionOption[] = useMemo(
    () =>
      draftQuestions.map((draft) => ({
        id: draft.id,
        scope: draft.data.scope ?? "buyer",
        label:
          draft.data.scope === "participant" && draft.data.type === QuestionType.Name
            ? t("dashboard", "stepQuestionsV4.attendeeName")
            : draft.data.body,
        subtitle: getQuestionTypeSubtitle(t, draft.data.type, draft.data.choices),
      })),
    [draftQuestions, t]
  );

  return (
    <FormStepContainer
      title={getStepQuestionsV4Wording(t, apiTicketing.formCategory).title}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
        nextButtonLabel:
          activeQuestions.length === 0 && !askForAddress && !formOffersCommunicationPreferences ? "skip" : "next",
      }}
      form={form}
      animatePreview={false}
    >
      <Box ref={formContainerRef} onFocus={handleCommunicationPreferencesFocus}>
        {isMemberInfoMode ? (
          <MembershipLinkedQuestionsListV4
            buyerQuestions={buyerQuestions}
            memberQuestions={attendeeQuestions}
            onReorderBuyer={handleBuyerDragEnd}
            onReorderMember={handleGuestDragEnd}
            onEdit={handleEditQuestion}
            onDelete={handleDeleteQuestion}
            onAddBuyerQuestion={() => handleOpenAddModal("buyer")}
            onAddMemberQuestion={() => handleOpenAddModal("participant")}
            additionalBuyerOptions={communicationPreferencesCard}
            askForAddress={askForAddress}
            onDeleteAddress={() => form.setValue("askForAddress", false, { shouldDirty: true })}
            getQuestionIndex={(question) => activeQuestions.findIndex((q) => q.localId === question.localId)}
            getQuestionLabel={(question) => {
              if (question.scope !== "participant") {
                return undefined;
              }
              if (question.type === QuestionType.Name) {
                return t("dashboard", "stepQuestionsV4.memberFullName");
              }
              if (question.type === QuestionType.Email) {
                return t("dashboard", "stepQuestionsV4.memberEmail");
              }
              return undefined;
            }}
            getLabelTooltip={getSmsLabelTooltip}
            getDeleteDisabledTooltip={getSmsDeleteDisabledTooltip}
          />
        ) : (
          <TicketingLinkedQuestionsListV4
            formCategory={apiTicketing.formCategory}
            buyerQuestions={buyerQuestions}
            attendeeQuestions={attendeeQuestions}
            onReorderBuyer={handleBuyerDragEnd}
            onReorderAttendee={handleGuestDragEnd}
            onEdit={handleEditQuestion}
            onDelete={handleDeleteQuestion}
            onAddBuyerQuestion={() => handleOpenAddModal("buyer")}
            onAddAttendeeQuestion={() => handleOpenAddModal("participant")}
            attendeeSuggestions={
              visibleSuggestions.length > 0 ? (
                <>
                  {visibleSuggestions.map((preset) => (
                    <AttendeeSuggestionRow
                      key={preset.id}
                      label={preset.label}
                      onDismiss={() => handleDismissSuggestion(preset)}
                      onAdd={() => handleAddSuggestion(preset)}
                    />
                  ))}
                </>
              ) : undefined
            }
            additionalBuyerOptions={communicationPreferencesCard}
            askForAddress={askForAddress}
            onDeleteAddress={() => form.setValue("askForAddress", false, { shouldDirty: true })}
            getQuestionIndex={(question) => activeQuestions.findIndex((q) => q.localId === question.localId)}
            getQuestionLabel={(question) => {
              if (question.scope !== "participant") {
                return undefined;
              }
              if (question.type === QuestionType.Name) {
                return t("dashboard", "stepQuestionsV4.attendeeName");
              }
              if (question.type === QuestionType.Email) {
                return t("dashboard", "stepQuestionsV4.attendeeEmail");
              }
              return undefined;
            }}
            getLabelTooltip={getSmsLabelTooltip}
            getDeleteDisabledTooltip={getSmsDeleteDisabledTooltip}
          />
        )}
      </Box>

      <AddQuestionModal
        isOpen={modalState.type === "add-question"}
        initialScope={addModalScope}
        formCategory={apiTicketing.formCategory}
        onClose={handleCloseModal}
        existingCustomProperties={existingCustomProperties}
        isLoadingProperties={isLoadingProperties}
        onAddSelected={handleAddSelected}
        onCreateFromScratch={openCreateModal}
        onSearch={handleQuestionSearch}
        showFullAddressOption={!askForAddress}
        showGuestFullAddressOption={!hasActiveGuestAddressQuestion}
        showPhoneNumberOption={!hasActiveBuyerPhoneQuestion && !hasSelectableBuyerPhoneProperty}
        showNotificationPhoneOption={showNotificationPhoneOption}
        notificationPhoneLabel={notificationPhoneLabel}
        showGuestPhoneNumberOption={!hasActiveGuestPhoneQuestion && !hasSelectableGuestPhoneProperty}
        buyerPresets={communicationPreferencesPresets}
        guestPresets={modalGuestPresets}
        showGuestTab={!isMemberInfoMode}
        titleOverride={
          isMemberInfoMode
            ? t(
                "dashboard",
                addModalScope === "participant"
                  ? "stepQuestionsV4.dialog.memberTitle"
                  : "stepQuestionsV4.dialog.buyerTitle"
              )
            : undefined
        }
        initialSelectedIds={addModalSelectedIds}
        draftQuestions={draftQuestionOptions}
        transitionDuration={isSwappingWithCreateModal ? 0 : undefined}
      />

      <CreateQuestionModal
        formKind="ticketing"
        key={editingQuestion?.localId ?? `new-${createModalScope ?? "buyer"}-${createModalPrefillBody ?? ""}`}
        isOpen={modalState.type === "create-new" || modalState.type === "edit"}
        transitionDuration={isSwappingWithCreateModal ? 0 : undefined}
        onClose={handleCloseModal}
        onSubmit={editingQuestion ? handleUpdateQuestion : handleCreateQuestion}
        isLoading={isSaving}
        isEditing={!!editingQuestion}
        questionNamesOnForm={questionNamesOnForm}
        matchableProperties={existingCustomProperties}
        showScopeSelector={!isMemberInfoMode}
        showRateAssignment={rateOptions.length > 1}
        canAddAttendeeName={canAddAttendeeName}
        showMaximumLimit={apiTicketing.formCategory !== TicketingFormCategory.Auction}
        showCommunicationPreferences={isCommunicationPreferencesEnabled && !formOffersCommunicationPreferences}
        communicationPreferenceFormType={CommunicationPreferenceFormType.Ticketing}
        onCommunicationPreferencesSaved={focusAddedCommunicationPreferences}
        formId={apiTicketing.id}
        formType={FormType.Ticketing}
        locale={apiTicketing.locale}
        rates={rateOptions}
        formCategory={apiTicketing.formCategory}
        disableNonEditableFields={!(editingQuestion?.isNew ?? true)}
        countAlreadyAnswered={editingQuestion?.countAlreadyAnswered}
        defaultValues={
          editingQuestion
            ? {
                body: editingQuestion.body,
                type: editingQuestion.type,
                required: editingQuestion.required,
                scope: editingQuestion.scope,
                askForAllRates: editingQuestion.askForAllRates,
                ratesAssigned: editingQuestion.ratesAssigned,
                isMaximumActive: editingQuestion.isMaximumActive,
                choices: editingQuestion.choices,
              }
            : { scope: createModalScope ?? "buyer", body: createModalPrefillBody }
        }
      />

      {isCommunicationPreferencesModalOpen && (
        <EditCommunicationPreferencesModal
          isOpen
          formId={apiTicketing.id}
          formType={CommunicationPreferenceFormType.Ticketing}
          formLocale={apiTicketing.locale}
          onClose={() => setIsCommunicationPreferencesModalOpen(false)}
        />
      )}
    </FormStepContainer>
  );
};
